import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { LoadingSpinner, ErrorMessage, EmptyState } from '../ui';
import { useCustomerReport, SectionProps } from '../../hooks/useCustomerAnalyticsSections';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor } from '../../utils/themeHelpers';
import { formatNumber } from '../../utils/formatters';
import ChartControls from '../ui/ChartControls';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';

interface CustomerReportSectionProps extends SectionProps {
  branchId: number;
  months?: number;
}

const CustomerReportSection: React.FC<CustomerReportSectionProps> = ({
  branchId,
  months = 12,
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { openGuide } = useChartGuide();
  const { data: reportData, isLoading, isError, error, refetch } = useCustomerReport(branchId, months);
  
  // Component metadata
  const componentId = 'customer-report-section';
  const componentTitle = 'گزارش تعداد مشتریانی که در یک سال گذشته خرید داشته یا نداشته‌اند';

  // Handle error callback
  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex flex-col items-center justify-center h-[400px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری گزارش…"
          />
        </div>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className="card-base card-padding">
        <ErrorMessage 
          message="خطا در دریافت گزارش مشتریان"
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (!reportData?.chartData || reportData.chartData.length === 0) {
    return (
      <div className="card-base card-padding">
        <EmptyState />
      </div>
    );
  }

  const barChartData = {
    labels: reportData.chartData.map(item => item.month).reverse(),
    datasets: [
      {
        label: 'تعداد مشتریانی که در یک سال گذشته خرید نداشته اند',
        data: reportData.chartData.map(item => item.customerLost).reverse(),
        backgroundColor: getChartBackgroundColor(1, currentTheme, 0.8),
        borderColor: getChartColors(currentTheme)[1],
        borderWidth: 2,
        borderRadius: 6
      },
      {
        label: 'تعداد مشتریانی که در یک سال گذشته خرید داشته اند',
        data: reportData.chartData.map(item => item.customerInLastYear).reverse(),
        backgroundColor: getChartBackgroundColor(0, currentTheme, 0.8),
        borderColor: getChartColors(currentTheme)[0],
        borderWidth: 2,
        borderRadius: 6
      }
    ]
  };

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <ChartControls
        componentId={componentId}
        componentTitle={componentTitle}
        componentType="chart"
        dashboardSource="customerAnalytics"
        onInfo={() => openGuide(CHART_GUIDES.customerReport)}
        onFavorite={() => handleFavoriteToggle({
          componentId,
          componentTitle,
          dashboardSource: 'customerAnalytics',
          componentType: 'chart',
          description: 'نمایش تعداد مشتریانی که در یک سال گذشته خرید داشته یا نداشته‌اند',
          props: { branchId, months, compact },
          gridLayout: {
            containerClass: 'grid grid-cols-1 lg:grid-cols-2 gap-6'
          }
        })}
        isFavorited={isFavorited(componentId)}
        chartRef={chartRef}
        className="mb-4"
      />
      <h3 className="title-card">{componentTitle}</h3>
      <div style={{ height: 400, width: '100%', padding: '20px 0' }}>
        <Bar
          data={barChartData}
          options={{
            ...getChartJsOptions(currentTheme),
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              legend: {
                ...getChartJsOptions(currentTheme).plugins?.legend,
                display: true
              },
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  label: function(context: any) {
                    const label = context.dataset.label || '';
                    const value = formatNumber(context.parsed.y);
                    const separator = '\u00A0:\u00A0';
                    return `${label}${separator}${value}`;
                  }
                }
              }
            },
            scales: {
              x: {
                ...getChartJsOptions(currentTheme).scales?.x,
                 ticks: {
                      ...getChartJsOptions(currentTheme).scales?.x?.ticks,
                      autoSkip: false,
                      maxRotation: 45,
                      minRotation: 45,
                    },
              },
              y: {
                ...getChartJsOptions(currentTheme).scales?.y,
                beginAtZero: true,
                ticks: {
                  ...getChartJsOptions(currentTheme).scales?.y?.ticks,
                  callback: function(value: any) {
                    return formatNumber(value);
                  }
                }
              }
            }
          }}
        />
      </div>
    </div>
  );
};

export default CustomerReportSection;